import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@kk/ui/components/Button';
import Icon from '@kk/ui/components/Icon';
import Input from '@kk/ui/components/Input';
import { cn } from 'cn';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import type api from '#/api';
import { useUpdateUserEmail } from '#/api/hooks/useUpdateUserEmail';

type VerifyEmailFormProps = {
  user: api.UserDto;
  companyInfo?: api.CompanyInfoResponse;
};

enum Fields {
  Name = 'name',
  Cvr = 'cvr',
  Email = 'email',
}

function VerifyEmailForm({ user, companyInfo }: VerifyEmailFormProps) {
  const { t } = useTranslation();

  const schema = useMemo(
    () =>
      z.object({
        [Fields.Name]: z.string().min(1),
        [Fields.Cvr]: z.string().min(1),
        [Fields.Email]: z.string().trim().email(t('profilePage.email.invalid')),
      }),
    [t],
  );

  const form = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: {
      name: user.name,
      cvr: companyInfo?.companyName ?? user.cvr?.toString() ?? '',
      email: user.email,
    },
  });

  const { mutate, isPending: isUpdateEmailPending } = useUpdateUserEmail();

  return (
    <div
      className={cn(
        'h-dvh items-center justify-center bg-white bg-cover bg-center bg-no-repeat pt-14 md:flex',
        'md:bg-[url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTUzNiIgaGVpZ2h0PSI5MTEiIHZpZXdCb3g9IjAgMCAxNTM2IDkxMSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBjbGlwLXBhdGg9InVybCgjY2xpcDBfMjBfNDIyMjEpIj48cmVjdCB3aWR0aD0iMTUzNiIgaGVpZ2h0PSI5MTMiIGZpbGw9IiM5RUQxRjAiLz48cGF0aCBkPSJNNDA0LjY5OSAtNDY5Ljc4N0M0MjIuNTA5IC00NTAuODQyIDk3OS44MTUgMzc0LjIyOCAxMjExLjEgNzE3LjkwNkw0MDQuNjk5IDIwMDUuNzkiIHN0cm9rZT0iI0IwREFGMSIgc3Ryb2tlLXdpZHRoPSI1ODguMjU1Ii8+PHBhdGggZD0iTTEwMDIuNzYgLTQ2OS43ODdDMTAyMC41MSAtNDUwLjg0MiAxNTc2LjEzIDM3NC4yMjggMTgwNi43MSA3MTcuOTA2TDEwMDIuNzYgMjAwNS43OSIgc3Ryb2tlPSIjQzJFMkYyIiBzdHJva2Utd2lkdGg9IjU4OC4yNTUiLz48L2c+PGRlZnM+PGNsaXBQYXRoIGlkPSJjbGlwMF8yMF80MjIyMSI+PHJlY3Qgd2lkdGg9IjE1MzYiIGhlaWdodD0iOTEzIiBmaWxsPSJ3aGl0ZSIvPjwvY2xpcFBhdGg+PC9kZWZzPjwvc3ZnPg==")]',
      )}
    >
      <FormProvider {...form}>
        <form
          className="rounded-xl bg-white p-6 md:max-w-150 md:p-14"
          onSubmit={form.handleSubmit(({ email }) => {
            if (email) mutate({ email });
          })}
        >
          <header className="space-y-2">
            <h1 className="typ-headline-xl">{t('verifyInfo.title')}</h1>
            <p className="typ-body text-neutral-100">
              {t('verifyInfo.subtitle')}
            </p>
          </header>
          <div className="mt-10 space-y-8">
            <Input
              label={t('verifyInfo.name')}
              type="text"
              disabled
              {...form.register(Fields.Name)}
            />
            <Input
              label={`${t('verifyInfo.company')}${!companyInfo?.companyName ? ' (cvr)' : ''}`}
              placeholder="Vælg virksomhed"
              disabled
              {...form.register(Fields.Cvr)}
              // value={companyInfo?.companyName ?? user.cvr}
            />
            <Input
              label={t('verifyInfo.email')}
              type="email"
              placeholder="mail@mail.dk"
              required
              error={form.formState.errors[Fields.Email]}
              {...form.register(Fields.Email)}
            />
            <div className="bg-blue-5 flex justify-between space-x-2 rounded-md p-4">
              <Icon
                type="info"
                solid
                size="small"
                className="shrink-0 grow-0 text-blue-100"
              />
              <p className="typ-body-xs font-semibold">
                {t('verifyInfo.infobox')}
              </p>
            </div>
          </div>
          <div className="mt-36 flex justify-end">
            <Button
              theme="primary"
              type="submit"
              disabled={!form.formState.isValid || isUpdateEmailPending}
              loading={isUpdateEmailPending}
            >
              {t('saveAndContinue')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
}

export default VerifyEmailForm;
